/**
 * Widget d'abonnement dynamique pour Shopify
 * Gère les selling plans et les prix dynamiques
 */

class SubscriptionWidget {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.variantId = container.dataset.variantId;
    this.sectionId = container.dataset.sectionId;
    this.basePrice = parseFloat(container.dataset.basePrice) / 100;
    this.comparePrice = parseFloat(container.dataset.comparePrice) / 100;
    
    this.radioButtons = container.querySelectorAll('input[type="radio"]');
    this.selectElement = container.querySelector('.subscription-widget__select');
    this.frequencySelect = container.querySelector('.subscription-widget__frequency-select');
    this.tomSelectInstance = null;
    this.currentVariant = null;
    this.sellingPlanSynchronized = true;
    this.container.subscriptionWidget = this;
    
    // Debug
    console.log('SubscriptionWidget initialized:', {
      productId: this.productId,
      variantId: this.variantId,
      sectionId: this.sectionId,
      basePrice: this.basePrice
    });
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.initTomSelect();
    
    // Initialiser le champ selling_plan
    this.updateSelectedPlan('');
    
    // Mise à jour initiale
    this.updateDisplayPrices();
    this.updateButtonText();
  }
  
  initTomSelect() {
    if (this.selectElement && typeof TomSelect !== 'undefined') {
      this.tomSelectInstance = new TomSelect(this.selectElement, {
        placeholder: "Choisissez une fréquence...",
        create: false,
        controlInput: false,
        onChange: (value) => {
          this.handleSelectChange({ target: { value: value } });
        }
      });
    }
  }
  
  bindEvents() {
    // Gestion des boutons radio
    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.handleRadioChange(e);
      });
    });
    
    // Écouter les changements de variante via les sélecteurs de variantes
    document.addEventListener('change', (e) => {
      if (e.target.matches('select[name="id"], input[name="id"]')) {
        const variantId = e.target.value;
        this.handleVariantIdChange(variantId);
      }
    });
    
    // Écouter les mises à jour du thème
    document.addEventListener('shopify:section:load', () => {
      requestAnimationFrame(() => {
        this.updateDisplayPrices();
        this.updateButtonText();
      });
    });
  }
  
  handleVariantIdChange(variantId) {
    // Récupérer les informations de la variante depuis le DOM
    const variantElement = document.querySelector(`[data-variant-id="${variantId}"]`);
    if (variantElement) {
      const price = parseFloat(variantElement.dataset.price) / 100;
      const comparePrice = variantElement.dataset.comparePrice ? parseFloat(variantElement.dataset.comparePrice) / 100 : 0;
      
      this.basePrice = price;
      this.comparePrice = comparePrice;
      this.variantId = variantId;
      
      // Mettre à jour les attributs data
      this.container.dataset.basePrice = price * 100;
      this.container.dataset.comparePrice = comparePrice * 100;
      this.container.dataset.variantId = variantId;
      
      // Mettre à jour l'affichage
      this.updateDisplayPrices();
      this.updateButtonText();
    }
  }
  
  handleVariantChange(variant) {
    if (!variant) {
      this.sellingPlanSynchronized = false;
      return false;
    }

    // Mettre à jour les prix de base
    this.basePrice = variant.price / 100;
    this.comparePrice = variant.compare_at_price ? variant.compare_at_price / 100 : 0;
    this.variantId = variant.id;
    this.currentVariant = variant;
    
    // Mettre à jour les attributs data
    this.container.dataset.basePrice = variant.price;
    this.container.dataset.comparePrice = variant.compare_at_price || 0;
    this.container.dataset.variantId = variant.id;
    
    // Mettre à jour l'affichage
    this.sellingPlanSynchronized = this.syncSelectedPlanWithCurrentVariant();
    this.updateDisplayPrices();
    this.updateButtonText();
    return this.sellingPlanSynchronized;
  }
  
  handleRadioChange(event) {
    const radio = event.target;
    const option = radio.closest('.subscription-widget__option');
    
    // Mettre à jour les classes actives
    this.container.querySelectorAll('.subscription-widget__option').forEach(opt => {
      opt.classList.remove('subscription-widget__option--active');
    });
    
    // Afficher/masquer le select de fréquence
    if (option && radio.value && this.frequencySelect) {
      option.classList.add('subscription-widget__option--active');
      this.frequencySelect.style.display = 'block';
    } else if (option) {
      option.classList.add('subscription-widget__option--active');
      this.frequencySelect.style.display = 'none';
    }
    
    // Mettre à jour le plan sélectionné et vérifier sa compatibilité avec la variante courante.
    this.sellingPlanSynchronized = this.currentVariant
      ? this.syncSelectedPlanWithCurrentVariant()
      : (this.updateSelectedPlan(radio.value), true);
    
    // Mettre à jour seulement le bouton
    this.updateButtonText();
    this.notifyProductInfoReadiness();
  }
  
  handleSelectChange(event) {
    const sellingPlanId = event.target.value;
    
    // Mettre à jour la valeur du radio button d'abonnement
    const subscriptionRadio = this.container.querySelector('#subscription-recurring-' + this.productId);
    if (subscriptionRadio) {
      subscriptionRadio.value = sellingPlanId;
    }
    
    // Mettre à jour le plan sélectionné
    this.sellingPlanSynchronized = this.currentVariant
      ? this.syncSelectedPlanWithCurrentVariant()
      : (this.updateSelectedPlan(subscriptionRadio?.checked ? sellingPlanId : ''), true);
    
    // Mettre à jour seulement le bouton
    this.updateButtonText();
    this.notifyProductInfoReadiness();
  }
  
  updateSelectedPlan(sellingPlanId) {
    this.getProductForms().forEach((productForm) => {
      this.syncSellingPlanInput(productForm, sellingPlanId);
    });
    
    this.container.dataset.selectedSellingPlan = sellingPlanId;
  }

  getProductForms() {
    return [
      document.getElementById(`product-form-${this.sectionId}`),
      document.getElementById(`product-form-sticky-${this.sectionId}`)
    ].filter(Boolean);
  }

  syncSellingPlanInput(productForm, sellingPlanId) {
    const inputs = Array.from(productForm.querySelectorAll('input')).filter((input) => input.name === 'selling_plan');
    const [sellingPlanInput, ...duplicates] = inputs;
    duplicates.forEach((input) => input.remove());

    if (!sellingPlanId) {
      sellingPlanInput?.remove();
      return;
    }

    const targetInput = sellingPlanInput || document.createElement('input');
    targetInput.type = 'hidden';
    targetInput.name = 'selling_plan';
    targetInput.value = sellingPlanId;

    if (!sellingPlanInput) {
      productForm.appendChild(targetInput);
    }
  }

  syncSelectedPlanWithCurrentVariant() {
    const selectedRadio = this.container.querySelector('input[type="radio"]:checked');
    const subscriptionRadio = this.container.querySelector('#subscription-recurring-' + this.productId);
    const isSubscriptionSelected = selectedRadio === subscriptionRadio;

    if (!isSubscriptionSelected) {
      this.updateSelectedPlan('');
      return true;
    }

    let sellingPlanId = this.getSelectedFrequencyValue() || selectedRadio.value;
    if (!this.isSellingPlanCompatibleWithCurrentVariant(sellingPlanId)) {
      sellingPlanId = this.getFirstAvailableSellingPlanIdForCurrentVariant();
      this.setFrequencyValue(sellingPlanId);
    }

    if (!sellingPlanId) {
      if (subscriptionRadio) subscriptionRadio.value = '';
      this.updateSelectedPlan('');
      return false;
    }

    if (subscriptionRadio) {
      subscriptionRadio.value = sellingPlanId;
    }

    this.updateSelectedPlan(sellingPlanId);
    return this.getProductForms().every((productForm) => {
      return String(productForm.querySelector('[name="selling_plan"]')?.value || '') === String(sellingPlanId);
    });
  }

  notifyProductInfoReadiness() {
    this.container
      .closest('product-info')
      ?.handleSubscriptionReadinessChange?.(this.sellingPlanSynchronized);
  }

  getSelectedFrequencyValue() {
    if (this.tomSelectInstance) {
      return this.tomSelectInstance.getValue();
    }

    return this.selectElement?.value || '';
  }

  setFrequencyValue(sellingPlanId) {
    if (this.tomSelectInstance) {
      this.tomSelectInstance.setValue(sellingPlanId, true);
    } else if (this.selectElement) {
      this.selectElement.value = sellingPlanId;
    }
  }

  isSellingPlanCompatibleWithCurrentVariant(sellingPlanId) {
    if (!sellingPlanId || !this.currentVariant?.selling_plan_allocations?.length) return false;

    return this.currentVariant.selling_plan_allocations.some((allocation) => {
      return String(allocation.selling_plan_id) === String(sellingPlanId);
    });
  }

  getFirstAvailableSellingPlanIdForCurrentVariant() {
    const availablePlanIds = new Set(
      (this.currentVariant?.selling_plan_allocations || []).map((allocation) => String(allocation.selling_plan_id))
    );

    return Array.from(this.selectElement?.options || []).find((option) => availablePlanIds.has(String(option.value)))?.value || '';
  }
  
  updateDisplayPrices() {
    const selectedRadio = this.container.querySelector('input[type="radio"]:checked');
    
    // L'achat unique affiche toujours le prix normal de la variante actuelle
    const oneTimePrice = this.container.querySelector('.subscription-widget__one-time-price');
    if (oneTimePrice) {
      oneTimePrice.textContent = this.formatPrice(this.basePrice);
    }
    
    // L'abonnement affiche toujours le prix normal barré + le prix remisé
    const subscriptionPrice = this.container.querySelector('.subscription-widget__subscription-price');
    const subscriptionComparePrice = this.container.querySelector('.subscription-widget__subscription-compare-price');
    
    if (subscriptionPrice && subscriptionComparePrice) {
      // Prix normal barré (toujours le prix de la variante actuelle)
      subscriptionComparePrice.textContent = this.formatPrice(this.basePrice);
      subscriptionComparePrice.style.display = 'inline';
      
      // Prix remisé - se met à jour selon la sélection
      if (selectedRadio && selectedRadio.value) {
        // Si l'abonnement est sélectionné, calculer le prix remisé
        const sellingPlanId = selectedRadio.value;
        const discountPercentage = this.getDiscountPercentage(sellingPlanId);
        
        if (discountPercentage > 0) {
          const discountedPrice = this.basePrice * (1 - discountPercentage / 100);
          subscriptionPrice.textContent = this.formatPrice(discountedPrice);
        } else {
          subscriptionPrice.textContent = this.formatPrice(this.basePrice);
        }
      } else {
        // Si l'achat unique est sélectionné, afficher le prix remisé du plan par défaut
        const defaultSellingPlanId = this.container.querySelector('.subscription-widget__select option:first-child')?.value;
        if (defaultSellingPlanId) {
          const discountPercentage = this.getDiscountPercentage(defaultSellingPlanId);
          if (discountPercentage > 0) {
            const discountedPrice = this.basePrice * (1 - discountPercentage / 100);
            subscriptionPrice.textContent = this.formatPrice(discountedPrice);
          } else {
            subscriptionPrice.textContent = this.formatPrice(this.basePrice);
          }
        }
      }
    }
  }
  
  getDiscountPercentage(sellingPlanId) {
    const select = this.container.querySelector(`option[value="${sellingPlanId}"]`);
    if (select && select.dataset.discount) {
      return parseFloat(select.dataset.discount);
    }
    return 0;
  }
  
  formatPrice(price) {
    if (window.FoxTheme && window.FoxTheme.Currency) {
      return FoxTheme.Currency.formatMoney(price * 100, FoxTheme.settings.moneyFormat);
    }
    
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(price);
  }
  
  updateButtonText() {
    const submitButton = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
    const stickyButton = document.querySelector(`#ProductSubmitButton-sticky-${this.sectionId}`);
    
    const selectedRadio = this.container.querySelector('input[type="radio"]:checked');
    
    let finalPrice = this.basePrice;
    const selectedSellingPlanId = this.getSelectedFrequencyValue();
    if (selectedRadio && selectedRadio.value && selectedSellingPlanId) {
      const discountPercentage = this.getDiscountPercentage(selectedSellingPlanId);
      if (discountPercentage > 0) {
        finalPrice = this.basePrice * (1 - discountPercentage / 100);
      }
    }
    
    const formattedPrice = this.formatPrice(finalPrice);
    
    // Mettre à jour le bouton principal
    if (submitButton && !submitButton.disabled) {
      const btnText = submitButton.querySelector('.btn__text');
      if (btnText) {
        btnText.innerHTML = FoxTheme.getProductSubmitContent(submitButton, formattedPrice);
      }
    }
    
    // Mettre à jour le bouton sticky
    if (stickyButton && !stickyButton.disabled) {
      const btnText = stickyButton.querySelector('.btn__text');
      if (btnText) {
        btnText.innerHTML = FoxTheme.getProductSubmitContent(stickyButton, formattedPrice);
      }
    }
  }

  destroy() {
    // Nettoyer l'instance Tom Select
    if (this.tomSelectInstance) {
      this.tomSelectInstance.destroy();
      this.tomSelectInstance = null;
    }
    
    delete this.container.subscriptionWidget;
  }
}

// Initialisation automatique et idempotente des widgets.
FoxTheme.initSubscriptionWidgets = (root = document) => {
  const widgets = [];
  if (root.matches?.('.subscription-widget')) widgets.push(root);
  root.querySelectorAll?.('.subscription-widget').forEach((widget) => widgets.push(widget));

  widgets.forEach(widget => {
    if (widget.subscriptionWidget) return;
    new SubscriptionWidget(widget);
  });
};

document.addEventListener('DOMContentLoaded', () => {
  FoxTheme.initSubscriptionWidgets();
});

// Support pour les sections dynamiques Shopify
document.addEventListener('shopify:section:load', (event) => {
  FoxTheme.initSubscriptionWidgets(event.target);
});

// Support pour les sections dynamiques Shopify (nettoyage)
document.addEventListener('shopify:section:unload', (event) => {
  const widget = event.target.querySelector('.subscription-widget');
  if (widget && widget.subscriptionWidget) {
    widget.subscriptionWidget.destroy();
  }
});

// Export pour utilisation globale
window.SubscriptionWidget = SubscriptionWidget;
